import React, { useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";
import { translations } from "../../../utils/Translation/staticTranslation";

Chart.register(...registerables);

const CategoryDistributionChart = ({ analyticsData }) => {
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.MerchantAnalytics || {};
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!analyticsData) return;
    if (chartInstance.current) chartInstance.current.destroy();

    const ctx = chartRef.current.getContext("2d");

    // ✅ Convert API analytics data → consistent array format
    const analyticsArray = Object.entries(analyticsData).map(([key, item]) => ({
      category: key,
      label: key
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join("-"),
      arabic: item.category_arabic,
      count: item.count,
      percentage: item.percentage,
    }));

    // Use Arabic labels if language is Arabic
    const categories =
      lang === "ar"
        ? analyticsArray.map((i) => i.arabic || i.label)
        : analyticsArray.map((i) => i.label);
    const data = analyticsArray.map((i) => i.count);
    const total = data.reduce((a, b) => a + b, 0);

    // ✅ Gradient colors (same as before)
    const gradients = data.map((_, index) => {
      const gradient = ctx.createLinearGradient(0, 0, 0, 500);
      const ratio = index / (data.length - 1);
      const darkColor = { r: 8, g: 9, b: 61 };
      const lightColor = { r: 125, g: 140, b: 207 };
      const r = Math.round(darkColor.r + (lightColor.r - darkColor.r) * ratio);
      const g = Math.round(darkColor.g + (lightColor.g - darkColor.g) * ratio);
      const b = Math.round(darkColor.b + (lightColor.b - darkColor.b) * ratio);
      const barColor = `rgb(${r}, ${g}, ${b})`;
      gradient.addColorStop(0, barColor);
      gradient.addColorStop(1, barColor);
      return gradient;
    });

    // ✅ Preserve vertical label plugin
    const verticalLabelPlugin = {
      id: "verticalLabels",
      afterDatasetsDraw(chart) {
        const { ctx, data } = chart;
        ctx.save();
        ctx.font = "500 9px -apple-system, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#000";

        chart.getDatasetMeta(0).data.forEach((bar, index) => {
          const label = data.labels[index];
          const { x, y } = bar.tooltipPosition();
          if (label) {
            ctx.save();
            ctx.translate(x, y - 50);
            ctx.rotate(-Math.PI / 2);
            ctx.fillText(label, 0, 0);
            ctx.restore();
          }
        });
        ctx.restore();
      },
    };

    // ✅ Preserve dashed grid style
    const dashedGridPlugin = {
      id: "dashedGrid",
      beforeDraw: (chart) => {
        const { ctx, scales } = chart;
        const y = scales.y;
        const x = scales.x;
        if (!y || !x) return;
        ctx.save();
        ctx.setLineDash([8, 5]);
        ctx.strokeStyle = "#E5E5E5";
        ctx.lineWidth = 1;
        y.ticks.forEach((_, i) => {
          const yPos = y.getPixelForTick(i);
          ctx.beginPath();
          ctx.moveTo(x.left, yPos);
          ctx.lineTo(x.right, yPos);
          ctx.stroke();
        });
        ctx.restore();
      },
    };

    // ✅ Create the chart
    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: categories,
        datasets: [
          {
            label: "Items",
            data,
            backgroundColor: gradients,
            borderRadius: {
              topLeft: 3,
              topRight: 3,
              bottomLeft: 0,
              bottomRight: 0,
            },
            borderSkipped: false,
            barPercentage: 1,
            categoryPercentage: 1,
            borderColor: "#2C4A7C",
            borderWidth: { right: 1, top: 0, bottom: 0 },
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 10, right: 10, left: 10, bottom: 50 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#fff",
            titleColor: "#111",
            bodyColor: "#111",
            borderColor: "#ddd",
            borderWidth: 1,
            padding: 10,
            displayColors: false,
            callbacks: {
              title(items) {
                const idx = items[0].dataIndex;
                const lang =
                  typeof window !== "undefined"
                    ? localStorage.getItem("language") || "en"
                    : "en";
                const label =
                  lang === "ar"
                    ? analyticsArray[idx].arabic
                    : analyticsArray[idx].label;
                return label;
              },
              label(context) {
                const value = context.parsed.y;
                const percent = ((value / total) * 100).toFixed(0);
                return `${
                  context.label
                }: ${value.toLocaleString()} | ${percent}%`;
              },
            },
          },
          title: {
            display: true,
            text:
              t.growth?.distribution_by_category || "Distribution by Category",
            align: "start",
            color: "#000",
            font: {
              size: 18,
              weight: "600",
              family: "-apple-system, sans-serif",
            },
            padding: { bottom: 50, top: 0, left: 0 },
          },
        },
        scales: {
          x: {
            ticks: { display: false },
            grid: { display: false },
            border: { display: false },
          },
          y: {
            beginAtZero: true,
            max: Math.max(...data) * 1.2,
            ticks: {
              stepSize: Math.ceil(Math.max(...data) / 5),
              color: "#666",
              font: { size: 11, family: "-apple-system, sans-serif" },
              padding: 8,
              callback: (val) => (val >= 1000 ? `${val / 1000}K` : val),
            },
            grid: { display: false },
            border: { display: false },
          },
        },
      },
      plugins: [verticalLabelPlugin, dashedGridPlugin],
    });
  }, [analyticsData, lang, t]);

  return (
    <div
      style={{
        background: "#FFFFFF",
        padding: "32px",
        borderRadius: "16px",
        border: "1px solid rgba(228, 229, 236, 1)",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ height: "500px" }}>
        <canvas ref={chartRef}></canvas>
      </div>
    </div>
  );
};

export default CategoryDistributionChart;
